import { IoCloseOutline, IoExitSharp } from "react-icons/io5";
import { colors, responsiveness, shadow } from "../../assets/styles/parameters";
import { useDispatch, useSelector } from "react-redux";

import { Link } from "react-router-dom";
import { SidebarPath } from "./Paths";
import { Text } from "../typography";
import styled from "styled-components";
import { toggleNav } from "../../redux/nav";
import { useAuth } from "react-oidc-context";

export const Sidebar = () => {
  const { open } = useSelector((state) => state.nav);
  const dispatch = useDispatch();
  const auth = useAuth();

  const handleLogout = async () => {
    dispatch({ type: "LOGOUT" });
    await auth.signoutRedirect({
      // Dynamically redirects to your local server or production URL!
      post_logout_redirect_uri: window.location.origin,
    });
  };

  return (
    <SideNav $open={open}>
      <Brand>
        {/* "O" monogram matching the main-site logo mark */}
        <Monogram aria-label="Oasis Home Healthcare">O</Monogram>
        {open && (
          <CloseButton
            onClick={() => dispatch(toggleNav())}
            aria-label="Close menu"
          >
            <IoCloseOutline />
          </CloseButton>
        )}
      </Brand>
      <ListIcon>
        {SidebarPath.map((s, i) => (
          // Removed the invalid 'open' prop from the <li>
          <li key={i} onClick={() => dispatch(toggleNav())}>
            <Link to={s.path} title={s.name}>
              {s.icon}
              <Text>{s.name}</Text>
            </Link>
          </li>
        ))}
      </ListIcon>
      {/* Changed to a proper button for semantic HTML and accessibility */}
      <LogoutButton onClick={handleLogout} title="Logout" aria-label="Logout">
        <IoExitSharp />
      </LogoutButton>
    </SideNav>
  );
};

export default Sidebar;

// --- STYLED COMPONENTS ---

// Used a transient prop ($open) so it doesn't bleed into the DOM
const SideNav = styled.nav`
  grid-area: sidebar;
  background: ${colors.blue};
  min-height: 99vh;
  width: 4%;
  position: fixed;
  left: 1%;
  top: 0.5%;
  border-radius: 5px;
  box-shadow: ${shadow.light};
  z-index: 10000;
  padding: 0.6rem 1rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  transition: all 0.3s ease-in;
  color: ${colors.white};

  img {
    width: 2rem;
  }

  p {
    display: none;
  }

  /* Desktop (>1024px): thin icon rail that expands to a labelled sidebar on hover */
  @media screen and (min-width: 1025px) {
    &:hover {
      width: 15%;

      img {
        width: 4rem;
      }

      p {
        display: block;
        line-height: 0;
      }
    }
  }

  /* Tablet + Mobile (≤1024px): full-screen overlay, toggled via hamburger button */
  @media screen and (max-width: 1024px) {
    display: ${({ $open }) => ($open ? "flex" : "none")};
    width: 100%;
    max-width: 320px; /* Drawer-style on tablet — doesn't cover full width */
    overflow-y: auto;
    height: 100vh;
    left: 0;
    top: 0;
    border-radius: 0;
    z-index: 10000;
    padding-top: 1rem;

    img {
      width: 4rem;
    }

    p {
      display: block;
    }
  }

  /* Mobile (≤480px): full-width overlay */
  @media screen and (max-width: ${responsiveness.mobile1}) {
    max-width: 100%;
  }
`;

const Brand = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: 1024px) {
    justify-content: space-between;
    padding: 0 1rem;
  }
`;

const CloseButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: ${colors.white};
  font-size: 2rem;
  cursor: pointer;
  padding: 0;

  @media screen and (max-width: 1024px) {
    display: block;
  }
`;

const ListIcon = styled.ul`
  list-style: none; /* Safely removes default bullets */
  padding: 0;
  width: 100%;

  li {
    margin-top: 3rem;

    & > a {
      display: flex;
      align-items: center;
      justify-content: center;
      text-decoration: none; /* Ensure links don't get default underlines */

      svg,
      p {
        color: ${colors.white};
        margin-bottom: 0;
      }

      p {
        margin-left: 1rem;
      }
    }
  }

  @media screen and (max-width: 1024px) {
    li {
      font-size: 1.5rem;
      margin-top: 1.3rem;
      padding: 0 1rem;
    }
  }
`;

const LogoutButton = styled.button`
  background: transparent;
  border: none;
  color: ${colors.white};
  display: flex;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: #ff6b6b; /* Subtle hover color to indicate logout/danger */
  }

  @media screen and (max-width: 1024px) {
    font-size: 2rem;
  }
`;

// "O" monogram pill — mirrors the Login page logo mark
const Monogram = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${colors.orange};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 800;
  color: ${colors.white};
  flex-shrink: 0;
  user-select: none;
`;
